import { createSignal, Show, type JSX } from "solid-js"
import { Portal } from "solid-js/web"
import { getCurrentUser, goToLogin } from "@/utils/zd-auth"
import { useDialog } from "@opencode-ai/ui/context/dialog"
import userAvatarImage from "@/assets/self-develop/avatar.png"

// ─── Icons ────────────────────────────────────────────────────────────────────

function SettingsIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" class="w-4 h-4 shrink-0">
      <circle cx="8" cy="8" r="2" />
      <path d="M8 1.5L8.8 3.2L10.6 2.8L10.4 4.7L12.1 5.6L11.3 7.3L12.9 8.4L11.3 9.5L12.1 11.2L10.4 12.1L10.6 14L8.8 13.6L8 15.3L7.2 13.6L5.4 14L5.6 12.1L3.9 11.2L4.7 9.5L3.1 8.4L4.7 7.3L3.9 5.6L5.6 4.7L5.4 2.8L7.2 3.2L8 1.5Z" />
    </svg>
  )
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4 shrink-0">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" x2="9" y1="12" y2="12" />
    </svg>
  )
}

// ─── Component ──────────────────────────────────────────────────────────────────

export function ZdUserMenu(): JSX.Element {
  const user = getCurrentUser()
  const dialog = useDialog()

  const [menuOpen, setMenuOpen] = createSignal(false)
  const [dropPos, setDropPos] = createSignal({ top: 0, right: 0 })

  let buttonRef: HTMLButtonElement | undefined

  const toggleMenu = () => {
    if (!menuOpen() && buttonRef) {
      const rect = buttonRef.getBoundingClientRect()
      setDropPos({ top: rect.top, right: window.innerWidth - rect.right })
    }
    setMenuOpen((v) => !v)
  }

  const openSettings = () => {
    setMenuOpen(false)
    void import("@/components/settings-v2").then((module) => {
      void dialog.show(() => <module.DialogSettings />)
    })
  }

  const handleLogout = () => {
    setMenuOpen(false)
    goToLogin()
  }

  const initial = () => (user?.username ?? "?").charAt(0).toUpperCase()

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={toggleMenu}
        aria-label={user ? `用户菜单：${user.username}` : "用户菜单"}
        aria-expanded={menuOpen()}
        aria-haspopup="menu"
        class="w-full flex items-center justify-between gap-3 px-3 py-3 rounded-lg hover:bg-black/[0.04] transition-colors cursor-pointer"
      >
        <span class="w-12 h-12 rounded-lg  border-slate-300 p-0.5 flex items-center justify-center shrink-0">
          <img src={userAvatarImage} alt={initial()} class="w-full h-full rounded-md object-cover" />
        </span>
        <span class="flex-1 text-left min-w-0">
          <span class="block text-[16px] font-medium text-slate-800 truncate">
            {user?.username ?? "未知用户"}
          </span>
          <Show when={user?.roleName}>
            <span class="block text-[12px] text-slate-500 truncate">
              {user!.roleName}
            </span>
          </Show>
        </span>
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.5" class="w-4 h-4 shrink-0 text-slate-400">
          <path d="M6 4L10 8L6 12" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      <Show when={menuOpen()}>
        <Portal>
          <div class="fixed inset-0 z-[9998]" onClick={() => setMenuOpen(false)} aria-hidden="true" />
          <div
            role="menu"
            style={{
              position: "fixed",
              bottom: `${window.innerHeight - dropPos().top + 6}px`,
              right: `${dropPos().right}px`,
            }}
            class="z-[9999] w-56 rounded-md border border-slate-200 bg-white p-1 shadow-md"
            onClick={(e: MouseEvent) => e.stopPropagation()}
          >
            <Show when={user}>
              <div class="px-2 py-2 mb-1 border-b border-slate-100">
                <div class="text-[13px] font-medium text-slate-800">{user!.username}</div>
                <Show when={user!.email}>
                  <div class="text-[12px] text-slate-500 truncate">{user!.email}</div>
                </Show>
                <Show when={user!.roleName}>
                  <div class="text-[12px] text-slate-500 mt-0.5">角色: {user!.roleName}</div>
                </Show>
              </div>
            </Show>

            <button
              type="button"
              role="menuitem"
              onClick={openSettings}
              class="w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
            >
              <SettingsIcon />
              设置
            </button>

            <div class="mt-1 pt-1 border-t border-slate-100">
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                class="w-full flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
              >
                <LogoutIcon />
                退出登录
              </button>
            </div>
          </div>
        </Portal>
      </Show>
    </>
  )
}
